import React from 'react';
import Button from '../common/Button';
import { RplDTO } from '../../services/apiClient';
import FormSection from '../common/FormSection';
import FileIcons from '../common/FileIcons';
import { RPL_QUESTIONS } from '../../constants/RplQuestionConstants';

interface RplApprovalProps {
  rpl: RplDTO;
  onClose: () => void
}

const FORM_SECTIONS = {
  questions: {
    id: 'questions',
    title: 'Questions',
  },
  files: {
    id: 'files',
    title: 'Supporting Documents',
  }
} as const;

const RplApproval: React.FC<RplApprovalProps> = ({
  rpl,
  onClose

}) => {
  const renderQuestionItem = (questionKey: keyof RplDTO) => {
    const question = RPL_QUESTIONS[questionKey];
    if (!question) return null;

    const value = rpl[questionKey] as boolean | undefined;
    
    return (
      <div key={questionKey} className="mb-4">
        <div
          className="text-sm font-medium text-gray-700 mb-1 cursor-help"
          title={question.questionText}
        >
          {question.label}
        </div>
        <div className="text-sm">
          <span
            className={`px-2 py-1 rounded ${
              value
                ? 'bg-green-100 text-green-800'
                : 'bg-red-100 text-red-800'
            }`}
          >
            {value ? 'Yes' : 'No'}
          </span>
        </div>
      </div>
    );
  };

  const renderFileItem = (questionKey: keyof RplDTO) => {
    const question = RPL_QUESTIONS[questionKey];
    if (!question?.requiresFile || !question.fileKey) return null;

    const fileId = rpl[question.fileKey as keyof RplDTO] as string | undefined;
    if (!fileId) return null;

    return (
      <div key={question.fileKey} className="mb-4 flex items-center justify-between">
        <div className="text-sm font-medium text-gray-700">
          {question.label} - Supporting Document
        </div>
        <FileIcons
          files={[
            {
              id: fileId,
              label: `${question.label} Document`,
            },
          ]}
        />
      </div>
    );
  };

  const answeredQuestions = Object.keys(RPL_QUESTIONS).filter(
    (key) => rpl[key as keyof RplDTO] !== undefined && rpl[key as keyof RplDTO] !== null
  );

  const questionsWithFiles = Object.keys(RPL_QUESTIONS).filter(
    (key) => {
      const question = RPL_QUESTIONS[key];
      return question.requiresFile && question.fileKey && rpl[question.fileKey as keyof RplDTO];
    }
  );

  return (
    <>
      <FormSection id={FORM_SECTIONS.questions.id} title={FORM_SECTIONS.questions.title}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {answeredQuestions.length > 0 ? (
            answeredQuestions.map((key) => renderQuestionItem(key as keyof RplDTO))
          ) : (
            <div className="col-span-2 text-sm text-gray-500 italic">
              No questions answered
            </div>
          )}
        </div>
      </FormSection>

      <FormSection id={FORM_SECTIONS.files.id} title={FORM_SECTIONS.files.title}>
        <div className="space-y-2">
          {questionsWithFiles.length > 0 ? (
            questionsWithFiles.map((key) => renderFileItem(key as keyof RplDTO))
          ) : (
            <div className="text-sm text-gray-500 italic">
              No files attached
            </div>
          )}
        </div>
      </FormSection>

      <div className="flex justify-end space-x-4">
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      </div>
    </>
  );
};

export default RplApproval;
